<template>
  <div class="page-selector">
    <FloatingPopup
      ref="floatingPopup"
      :minWidth="220"
      :maxWidth="340"
      ariaLabel="Page selector"
    >
      <template #trigger="{ toggle, isOpen }">
        <button
          class="selector-trigger"
          @click="toggle()"
          :title="triggerLabel"
          aria-haspopup="listbox"
          :aria-expanded="isOpen"
        >
          <Layers :size="14" />
          <span class="trigger-label">{{ triggerLabel }}</span>
          <ChevronDown :size="12" :class="{ rotated: isOpen }" />
        </button>
      </template>

    <template #content>
      <div class="selector-dropdown" role="listbox">
        <!-- Special pages -->
        <div class="dropdown-group-label">Special</div>
        <button
          class="dropdown-item special-item"
          :class="{ active: apiConfig.selectedPage === SpecialPage.SEARCH }"
          @click="selectPage(SpecialPage.SEARCH)"
          role="option"
          :aria-selected="apiConfig.selectedPage === SpecialPage.SEARCH"
        >
          <Search :size="12" />
          Search
        </button>
        <button
          class="dropdown-item special-item"
          :class="{ active: apiConfig.selectedPage === SpecialPage.CURRENT }"
          @click="selectPage(SpecialPage.CURRENT)"
          role="option"
          :aria-selected="apiConfig.selectedPage === SpecialPage.CURRENT"
        >
          <Monitor :size="12" />
          Current page
        </button>

        <!-- Page tree -->
        <template v-if="apiConfig.pages && flatPages.length > 0">
          <div class="dropdown-separator" />
          <template v-for="item in flatPages" :key="item.page.page_key">
            <!-- Notebook header (non-selectable) -->
            <div
              v-if="item.isNotebook"
              class="dropdown-group-label"
              :style="{ paddingLeft: `${8 + item.depth * 12}px` }"
            >
              <FolderOpen :size="12" />
              {{ item.page.name }}
            </div>
            <!-- Leaf page (selectable) -->
            <button
              v-else
              class="dropdown-item page-item"
              :class="{
                active: isSelectedPage(item.page),
                'is-active-in-client': isActiveInClient(item.page),
              }"
              :disabled="isDisabled(item.page)"
              :style="{ paddingLeft: `${8 + item.depth * 12}px` }"
              @click="selectPage(item.page)"
              role="option"
              :aria-selected="isSelectedPage(item.page)"
              :aria-disabled="isDisabled(item.page)"
              :title="item.page.name"
            >
              <component :is="pageIcon(item.page)" :size="12" />
              <span class="page-name">{{ item.page.name }}</span>
              <span
                v-if="pageCount(item.page) !== undefined"
                class="page-badge"
                :class="{ empty: pageCount(item.page) === 0 }"
              >{{ pageCount(item.page) }}</span>
            </button>
          </template>
        </template>
        <div v-else-if="apiConfig.pages === null" class="dropdown-loading">Loading pages…</div>
      </div>
    </template>
    </FloatingPopup>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import {
  ChevronDown,
  Download,
  FolderOpen,
  HardDrive,
  Layers,
  Monitor,
  Search,
  Star,
} from "lucide-vue-next";
import type { Component } from "vue";
import { SpecialPage, useApiStore } from "@/stores/api";
import type { SelectedPage } from "@/stores/api";
import type { Page } from "@/requests/api";
import { PageType } from "@/requests/api";
import FloatingPopup from "./FloatingPopup.vue";

const apiConfig = useApiStore();
const floatingPopup = ref<InstanceType<typeof FloatingPopup>>();

type FlatPage = {
  page: Page;
  depth: number;
  isNotebook: boolean;
};

function flattenPages(page: Page, depth: number, skipSelf: boolean = false): FlatPage[] {
  const result: FlatPage[] = [];
  const isNotebook = page.page_type === PageType.PAGE_OF_PAGES;

  if (!skipSelf) {
    result.push({ page, depth, isNotebook });
  }

  if (page.pages && page.pages.length > 0) {
    const childDepth = skipSelf ? depth : depth + 1;
    for (const child of page.pages) {
      result.push(...flattenPages(child, childDepth));
    }
  }

  return result;
}

const flatPages = computed<FlatPage[]>(() => {
  if (!apiConfig.pages) return [];
  // Skip the root "top page notebook" — show its children starting at depth 0
  return flattenPages(apiConfig.pages, 0, true);
});

function pageCount(page: Page): number | undefined {
  return apiConfig.pageFileCounts[page.page_key];
}

function isDisabled(page: Page): boolean {
  const count = pageCount(page);
  return count !== undefined && count === 0;
}

function isSelectedPage(page: Page): boolean {
  const sel = apiConfig.selectedPage;
  if (!sel || typeof sel !== "object") return false;
  return (sel as Page).page_key === page.page_key;
}

function isActiveInClient(page: Page): boolean {
  if ("focused" in page) return (page as any).focused === true;
  if ("selected" in page) return (page as any).selected === true;
  return false;
}

function pageIcon(page: Page): Component {
  switch (page.page_type) {
    case PageType.GALLERY_DOWNLOADER:
    case PageType.URL_DOWNLOADER:
      return Download;
    case PageType.HARD_DRIVE_IMPORT:
      return HardDrive;
    case PageType.DUPLICATES:
      return Star;
    default:
      return Search;
  }
}

function selectPage(page: Page | SpecialPage) {
  apiConfig.selectedPage = page as SelectedPage;
  floatingPopup.value?.close();
}

const triggerLabel = computed<string>(() => {
  const sel = apiConfig.selectedPage;
  if (sel === null || sel === undefined) return "Loading…";
  if (sel === SpecialPage.SEARCH) return "Search";
  if (sel === SpecialPage.CURRENT) return "Current page";
  return (sel as Page).name ?? "Page";
});
</script>

<style scoped lang="scss">
.page-selector {
  display: inline-flex;
  align-items: center;
}

.selector-trigger {
  display: flex;
  align-items: center;
  gap: 4px;
  background: transparent;
  border: none;
  color: var(--text-primary);
  cursor: pointer;
  padding: 0 0.5em;
  height: 100%;
  font-size: inherit;
  white-space: nowrap;
  max-width: 12rem;

  &:hover { color: var(--accent); }

  .trigger-label {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 0.85em;
  }

  svg.rotated {
    transform: rotate(180deg);
    transition: transform 120ms ease;
  }
}

.selector-dropdown {
  display: flex;
  flex-direction: column;
}

.dropdown-group-label {
  padding: 6px 8px 3px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  color: var(--text-accent);
  letter-spacing: 0.04em;
  display: flex;
  align-items: center;
  gap: 4px;
  opacity: 0.8;
  pointer-events: none;
}

.dropdown-separator {
  height: 1px;
  background: var(--border);
  margin: 3px 0;
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 6px 8px;
  background: transparent;
  border: none;
  color: var(--text-primary);
  cursor: pointer;
  font-size: 0.85rem;
  text-align: left;
  width: 100%;
  white-space: nowrap;

  &:hover:not(:disabled) { background: var(--btn-hover-bg); color: var(--accent); }
  &.active { background: color-mix(in srgb, var(--accent) 12%, var(--bg-elevated)); color: var(--accent); font-weight: 600; }
  &.is-active-in-client::before { content: "▶ "; font-size: 0.7em; opacity: 0.6; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
}

.page-name {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
}

.page-badge {
  flex-shrink: 0;
  background: var(--bg-elevated);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 0 5px;
  font-size: 0.7rem;
  font-variant-numeric: tabular-nums;

  &.empty {
    opacity: 0.45;
  }
}

.dropdown-loading {
  padding: 10px 12px;
  font-size: 0.85rem;
  opacity: 0.6;
}
</style>
